import React, { useState, useEffect } from 'react'
import { MessagePure, IconLanguageHLPure } from '../../pures'
import './on-boarding-validation-code.css'
import { HeaderHLPure } from '../../pures'
import { LocalStorageService } from '../../../services'
import { useTranslation } from 'react-i18next'

/**
 * @author Edgar Escobedo
 * @description Onboarding validation code view component
 * @createdDate 13-01-2026
 * @typedef {object} Props
 * @property {function} t - Translation function
 * @property {string} emailMasked - The masked email address to which the validation code was sent
 * @property {object} inputOTP - Ref for the OTP input field
 * @property {function} handleResendEmail - Function to handle resending the validation code email
 * @property {string} validationCode - The current value of the validation code input
 * @property {boolean} focusedOTP - Indicates if the OTP input field is focused
 * @property {function} handleClickOTP - Function to handle click event on the OTP input field
 * @property {function} handleFocusOTP - Function to handle focus event on the OTP input field
 * @property {function} handleBlurOTP - Function to handle blur event on the OTP input field
 * @property {function} handleChangeOTP - Function to handle change event on the OTP input field
 * @property {function} handleKeyUpOTP - Function to handle key up event on the OTP input field
 * @property {function} handleValidateCode - Function to handle validation of the code
 * @param {Props} props
 * @returns {JSX.Element} - The OnboardingValidationCodeView component
 */
const OnboardingValidationCodeView = ({
  t,
  emailMasked,
  inputOTP,
  handleResendEmail,
  validationCode,
  focusedOTP,
  handleClickOTP,
  handleFocusOTP,
  handleBlurOTP,
  handleChangeOTP,
  handleKeyUpOTP,
  handleValidateCode,
  languageIdFromBackend
}) => {
  const { i18n } = useTranslation()
  const localStorageService = new LocalStorageService()
  const logoLenguage = `${process.env.REACT_APP_AWS_S3_BUCKET_URL}${process.env.REACT_APP_ICONS_LANGUAGE_WORLD}`

  const getLanguageFromId = (languageId) => {
    if (languageId === 1) return 'es'
    if (languageId === 2) return 'en'
    return null
  }

  const systemLanguageCode = (i18n?.language || 'es').toLowerCase().startsWith('es') ? 'es' : 'en'

  const [currentLanguage, setCurrentLanguage] = useState(systemLanguageCode)

  const languages = [
    { label: 'ES', language: 'es' },
    { label: 'EN', language: 'en' }
  ]

  const changeLanguage = (language) => {
    if (!language) return
    setCurrentLanguage(language)
    localStorageService.setLocalStorageItem('preferredLanguage', language)
    i18n.changeLanguage(language)
  }

  useEffect(() => {
    if (languageIdFromBackend) {
      const backendLanguage = getLanguageFromId(languageIdFromBackend)
      if (backendLanguage) {
        setCurrentLanguage(backendLanguage)
        i18n.changeLanguage(backendLanguage)
        localStorageService.setLocalStorageItem('preferredLanguage', backendLanguage)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languageIdFromBackend])

  return (
    <>
      <div className='onBoardingRequestValidationCodeView-header'>
        <HeaderHLPure title={t('onboardingDataUserView.header.validate.code')} />

      </div>
      <div className='onboardingValidationCodeView-wrapper'>
        <div className='onboardingValidationCodeView-container'>
          <div className='onboardingValidationCodeView-translate'>
            <img
              alt='Idioma'
              src={logoLenguage}
              style={{ width: '24px', height: '24px' }}
            />
            {languages.map((lang, index) => (
              <React.Fragment key={lang.language}>
                <div
                  style={{ cursor: 'pointer' }}
                  onClick={() => changeLanguage(lang.language)}
                >
                  <IconLanguageHLPure
                    label={lang.label}
                    status={currentLanguage === lang.language}
                  />
                </div>
                {index < languages.length - 1 && (
                  <span style={{ color: '#666' }}>|</span>
                )}
              </React.Fragment>
            ))}
          </div>
          <MessagePure
            input={inputOTP}
            onReSendMessage={(e) => handleResendEmail(e)}
            validationCode={validationCode}
            focused={focusedOTP}
            cellphoneNumber={'email'}
            description={t('requestOTPView.description', { userEmail: emailMasked })}
            handleClick={(e) => handleClickOTP(e)}
            handleFocus={(e) => handleFocusOTP(e)}
            handleBlur={(e) => handleBlurOTP(e)}
            handleChange={(e) => handleChangeOTP(e)}
            handleKeyUp={(e) => handleKeyUpOTP(e)}
            onlyEmail={true}
            labelBtnOnlyEmail={t('messagePure.send.email.only.email')}
            labelTitle={t('messagePure.onboarding.title.only.email')}
            onVerify={handleValidateCode}
          />
        </div>
      </div>
    </>
  )
}

export { OnboardingValidationCodeView as default }